import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import { useSources } from '../context/SourcesContext';
import { useToast } from '../context/ToastContext';
import { SourceFormAddEntry, SourceFormDialog, SourceFormEditData, SourceFormMode } from '../components/dialogs/SourceFormDialog';
import { ManagedSource } from '../types';
import {
  ActionsMenu,
  Button,
  ChevronLeft,
  ConfirmDialog,
  DataTable,
  DataTableColumn,
  DateRangePicker,
  DateRangeValue,
  EmptyState,
  FilterButton,
  PageLoader,
  Plus,
  SearchInput,
  Toggle,
  Tooltip,
  Warning,
} from '../uikit';
import { sourceDisplayName } from './feedFilters';
import {
  DEFAULT_SOURCES_TABLE_FILTERS,
  SourcesStatusFilter,
  SourcesTableFilters,
  isInvalidSource,
  sourcesToolbarAction,
  toSourcesTableParams,
} from './sourcesTable';

const PAGE_SIZE = 25;
const MUTATION_TOAST_AUTO_HIDE_MS = 3000;

// COM-647 Task 4: `Source.createdAt` has no timezone-chip requirement worth
// chasing (Figma draws one, but so does the rest of this app's date
// rendering not bother with it - `allArticlesFeed.ts`'s own
// `formatFeedArticleDate` is the same UTC `dd.mm.yyyy`, no time-of-day, no
// zone). Kept local to this page rather than promoted to a shared helper -
// nothing else needs a source's OWN creation date formatted this way.
function formatSourceCreatedAt(createdAt: string): string {
  const parsed = new Date(createdAt);
  if (Number.isNaN(parsed.getTime())) return '—';
  const dd = String(parsed.getUTCDate()).padStart(2, '0');
  const mm = String(parsed.getUTCMonth() + 1).padStart(2, '0');
  const yyyy = parsed.getUTCFullYear();
  return `${dd}.${mm}.${yyyy}`;
}

interface StatusFilterMenuProps {
  value: SourcesStatusFilter;
  onChange: (value: SourcesStatusFilter) => void;
}

const STATUS_OPTIONS: { value: SourcesStatusFilter; label: string }[] = [
  { value: 'both', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

// Same composition as AllArticlesPage's own `SortMenu`/the kit's
// `DateRangePicker` - a `FilterButton` trigger opening a `Popover`/
// `MenuList` - kept local to this page (not promoted to the kit) since this
// exact three-option Active/Inactive/Both list is specific to the Sources
// Settings table, not a general-purpose control.
function StatusFilterMenu({ value, onChange }: StatusFilterMenuProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = (e: MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const close = () => setAnchorEl(null);

  const current = STATUS_OPTIONS.find((option) => option.value === value) ?? STATUS_OPTIONS[0];

  return (
    <>
      <FilterButton label={value === 'both' ? 'Status' : current.label} active={value !== 'both'} onClick={open} />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { p: 1, minWidth: 180 } }}
      >
        <MenuList dense>
          {STATUS_OPTIONS.map((option) => (
            <MenuItem
              key={option.value}
              selected={option.value === value}
              onClick={() => {
                onChange(option.value);
                close();
              }}
              sx={{ fontSize: 14, borderRadius: '6px' }}
            >
              {option.label}
            </MenuItem>
          ))}
        </MenuList>
      </Popover>
    </>
  );
}

interface FormDialogState {
  mode: SourceFormMode;
  source: ManagedSource | null;
}

// COM-647 Task 4: Settings / Regulations Scanner / Sources (Figma
// 1883:10111 and siblings). Reached from the "Sources Settings" button at
// the bottom of the All Articles page's own Sources filter dropdown
// (SourcesFilter.tsx) - see App.tsx's own route comment for why this is a
// standalone route rather than nested under `/settings` (that path is the
// project questionnaire, untouched by this ticket).
const SourcesSettingsPage: React.FC = () => {
  const {
    selectedProject,
    canManageProject,
    fetchManagedSources,
    updateSource,
    setSourceMonitored,
    deleteSources,
    addSources,
  } = useSources();
  const { showToast } = useToast();
  const navigate = useNavigate();

  // Fix wave (review, Important 1): this page's read event
  // (`getManagedSources`, backend/src/index.ts) deliberately uses
  // `requireProjectAccess`, not `requireProjectOwner` - a VIEWER is meant to
  // see this table, same as `getSources`/`getCategories`. The four
  // mutations (`updateSource`/`setSourceMonitored`/`deleteSources`/
  // `addSources`) are all `requireProjectOwner`-gated on the backend, so
  // without a client-side gate a VIEWER could reach every live control here
  // (toggle, Add, Edit, Delete) and have each one refused server-side - the
  // toggle springs back, a toast appears detached from whatever the user
  // touched. Chosen fix: read-only for a VIEWER (not hiding the entry-point
  // button, not a no-permission block) - the read genuinely allows it, and
  // it's the most useful of the three options for something a VIEWER may
  // legitimately want to check (is a source active? what's its url?).
  // Mirrors the disabled/hidden-control convention `UserSettingsDialog.tsx`
  // already uses for its own per-row controls under `canManageProject()`.
  const canManage = canManageProject();

  const [filters, setFilters] = useState<SourcesTableFilters>(DEFAULT_SOURCES_TABLE_FILTERS);
  const [searchInput, setSearchInput] = useState('');
  // `DateRangePicker` needs a preset alongside the raw range purely for its
  // OWN trigger label/highlight - `sourcesTable.ts`'s filter state (and the
  // params it maps to) only ever cares about the resolved `from`/`to`, same
  // separation `feedFilters.ts` documents for why its own default preset is
  // 'custom'.
  const [datePreset, setDatePreset] = useState<DateRangeValue['preset']>('custom');

  const [rows, setRows] = useState<ManagedSource[]>([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());

  const hasLoadedRef = useRef(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [formDialog, setFormDialog] = useState<FormDialogState | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formRefusalError, setFormRefusalError] = useState<string | null>(null);

  const [deleteTargetIds, setDeleteTargetIds] = useState<string[] | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  // Plain functions on SourcesContext's value get a fresh identity on every
  // provider render (same reasoning `AllArticlesPage.tsx`'s own
  // `fetchArticlesFeedRef` documents) - refs sidestep needing any of them in
  // an effect/callback dependency array.
  const fetchManagedSourcesRef = useRef(fetchManagedSources);
  fetchManagedSourcesRef.current = fetchManagedSources;

  const requestSeqRef = useRef(0);

  const runFetch = useCallback(
    (kind: 'reset' | 'append', offset: number, filtersSnapshot: SourcesTableFilters) => {
      const seq = ++requestSeqRef.current;
      if (kind === 'append') {
        setLoadingMore(true);
      } else if (hasLoadedRef.current) {
        setRefreshing(true);
      }

      fetchManagedSourcesRef
        .current({ limit: PAGE_SIZE, offset, ...toSourcesTableParams(filtersSnapshot) })
        .then(
          (page) => {
            if (requestSeqRef.current !== seq) return; // superseded by a newer request
            hasLoadedRef.current = true;
            setInitialLoading(false);
            setRefreshing(false);
            setLoadingMore(false);
            setTotal(page.total);
            setRows((prev) => (kind === 'append' ? [...prev, ...page.sources] : page.sources));
            // A filter/sort/search change (a 'reset' fetch) replaces the
            // whole row set - any selection made against the PREVIOUS set
            // has nothing trustworthy left to refer to. An 'append' (plain
            // endless scroll on the SAME filters) must not touch it.
            if (kind === 'reset') {
              setSelected(new Set());
            }
          },
          (err: unknown) => {
            if (requestSeqRef.current !== seq) return;
            setInitialLoading(false);
            setRefreshing(false);
            setLoadingMore(false);
            const message = err instanceof Error ? err.message : 'Failed to load sources. Please try again.';
            showToast(message, 'error');
          }
        );
    },
    [showToast]
  );

  // Debounce the raw search input ~300ms before it becomes a filter (and
  // triggers a reset fetch below) - same technique/duration as
  // AllArticlesPage's own search box.
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((current) => {
        const trimmed = searchInput.trim();
        return current.search === trimmed ? current : { ...current, search: trimmed };
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // First load, and every filter change thereafter (search/status/date/
  // sort - `filters` is a fresh object on every `setFilters` call, so
  // reference equality here is exactly "did anything change").
  useEffect(() => {
    if (!selectedProject) return;
    runFetch('reset', 0, filters);
  }, [selectedProject, filters, runFetch]);

  const canFetchMore = rows.length < total;
  const onEndReached = useCallback(() => {
    if (!canFetchMore || loadingMore || refreshing || initialLoading) return;
    runFetch('append', rows.length, filters);
  }, [canFetchMore, loadingMore, refreshing, initialLoading, rows.length, filters, runFetch]);

  const handleToggleMonitored = (source: ManagedSource, isMonitored: boolean) => {
    setTogglingIds((prev) => new Set(prev).add(source.id));
    setSourceMonitored(source.id, isMonitored).then(
      (updated) => {
        setTogglingIds((prev) => {
          const next = new Set(prev);
          next.delete(source.id);
          return next;
        });
        // Fix wave (review, Item 5): a plain local row swap is right when
        // the Status filter is 'both' - the row still belongs in the
        // result set no matter which way it just flipped. But with the
        // filter set to Active/Inactive, flipping a row's status can make
        // it no longer match the filter it's currently displayed under
        // (e.g. toggling an Active row to Inactive while filtered to
        // "Active" would otherwise leave it on screen reading "Inactive"
        // inside an Active-only table). Refetching from offset 0 in that
        // case re-applies the SAME filter the row just stopped matching, so
        // it's correctly dropped (or, for the opposite flip, correctly
        // appears) rather than lingering from a stale local patch.
        if (filters.statusFilter === 'both') {
          setRows((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));
        } else {
          // Side effect worth flagging: a 'reset' fetch collapses any pages
          // appended via endless scroll back to just page one and clears the
          // current row selection - the right trade here (the toggled row
          // may no longer belong under this status filter at all), but this
          // is the same page a prior commit specifically preserved off-view
          // selections for `DataTable`, so a reader may otherwise wonder.
          runFetch('reset', 0, filters);
        }
      },
      (err: Error) => {
        setTogglingIds((prev) => {
          const next = new Set(prev);
          next.delete(source.id);
          return next;
        });
        showToast(err.message || 'Failed to update the source. Please try again.', 'error');
      }
    );
  };

  const openAddDialog = () => {
    setFormRefusalError(null);
    setFormDialog({ mode: 'add', source: null });
  };

  const openEditDialog = (source: ManagedSource) => {
    setFormRefusalError(null);
    setFormDialog({ mode: 'edit', source });
  };

  const closeFormDialog = () => {
    if (formSubmitting) return;
    setFormDialog(null);
    setFormRefusalError(null);
  };

  const handleSubmitAdd = (entries: SourceFormAddEntry[]) => {
    setFormSubmitting(true);
    setFormRefusalError(null);
    addSources(entries).then(
      () => {
        setFormSubmitting(false);
        setFormDialog(null);
        // Refresh from offset 0 so the new row(s) - and an updated `total` -
        // show up immediately, same as a filter change would.
        runFetch('reset', 0, filters);
      },
      (err: Error) => {
        setFormSubmitting(false);
        setFormRefusalError(err.message || 'Failed to add the source(s). Please try again.');
      }
    );
  };

  const handleSubmitEdit = (data: SourceFormEditData) => {
    if (!formDialog?.source) return;
    const sourceId = formDialog.source.id;
    setFormSubmitting(true);
    setFormRefusalError(null);
    updateSource(sourceId, data).then(
      (updated) => {
        setFormSubmitting(false);
        setFormDialog(null);
        setRows((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));
        // AC: a toast on successful edit.
        showToast('Source updated', 'success', { autoHideMs: MUTATION_TOAST_AUTO_HIDE_MS });
      },
      (err: Error) => {
        setFormSubmitting(false);
        setFormRefusalError(err.message || 'Failed to update the source. Please try again.');
      }
    );
  };

  const requestDelete = (ids: string[]) => setDeleteTargetIds(ids);
  const cancelDelete = () => {
    if (deleteSubmitting) return;
    setDeleteTargetIds(null);
  };

  const confirmDelete = () => {
    if (!deleteTargetIds) return;
    setDeleteSubmitting(true);
    deleteSources(deleteTargetIds).then(
      (result) => {
        setDeleteSubmitting(false);
        setDeleteTargetIds(null);
        // Fix wave (review, Important 3): this used to filter `rows` by
        // `result.sourceIds` (what we ASKED to delete) but decrement `total`
        // by `result.deletedCount` (what the backend ACTUALLY deleted) -
        // `dbService.ts#deleteSources`'s own comment explains why these can
        // differ (a co-owner already deleted one of the selected rows
        // between the confirm dialog and this reply). Splicing `rows` by
        // the requested ids in that case drops a row from the table that
        // was never actually removed, and `rows.length`/`total` disagree
        // afterwards, which then feeds a wrong offset into the next
        // endless-scroll fetch. Refetching from offset 0 (same as a
        // successful add already does) makes `rows` and `total` both come
        // from the SAME source of truth - the server's current state - so
        // there is nothing left to reconcile by hand. This also naturally
        // clears `selected` (`runFetch`'s own 'reset' branch), which is
        // correct here: every id that was selected either got deleted (so
        // selecting it further is meaningless) or turned out to already be
        // gone (so it's not in the refetched rows either). Side effect worth
        // flagging alongside that: this also collapses any pages appended
        // via endless scroll back to just page one, same trade as the
        // toggle handler above, and for the same reason - `rows` has to
        // come from a single fresh read, not a patched-up prior one.
        runFetch('reset', 0, filters);
        // AC: a toast on successful delete - one message for a single
        // source, another for several (the brief's own wording). A
        // `deletedCount` of 0 (every selected row was already gone) is NOT
        // a success - it must not render the green "N sources deleted"
        // toast for an action that removed nothing.
        if (result.deletedCount === 0) {
          showToast('No sources were deleted - they may have already been removed.', 'info', {
            autoHideMs: MUTATION_TOAST_AUTO_HIDE_MS,
          });
        } else {
          showToast(
            result.deletedCount === 1 ? 'Source deleted' : `${result.deletedCount} sources deleted`,
            'success',
            { autoHideMs: MUTATION_TOAST_AUTO_HIDE_MS }
          );
        }
      },
      (err: Error) => {
        setDeleteSubmitting(false);
        showToast(err.message || 'Failed to delete. Please try again.', 'error');
      }
    );
  };

  const toolbarAction = sourcesToolbarAction(selected.size);

  const noFiltersApplied =
    filters.search === '' && filters.statusFilter === 'both' && !filters.dateRange.from && !filters.dateRange.to;

  const columns: DataTableColumn<ManagedSource>[] = [
    {
      key: 'name',
      title: 'Source Name',
      sortable: true,
      render: (row) => (
        <Typography
          fontSize={14}
          fontWeight={500}
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 280 }}
        >
          {sourceDisplayName(row)}
        </Typography>
      ),
    },
    {
      key: 'url',
      title: 'Source Link',
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            component="a"
            href={row.url}
            target="_blank"
            // COM-641's review caught exactly this omission on another
            // model-supplied link - required whenever a link opens in a new
            // tab, so the new tab can't reach back into this page via
            // `window.opener`.
            rel="noopener noreferrer"
            sx={{
              fontSize: 14,
              color: '#01ADEF',
              textDecoration: 'underline',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 220,
              display: 'inline-block',
            }}
          >
            {row.url}
          </Typography>
          {/* Figma's red alert badge - routes through `isInvalidSource`
              (sourcesTable.ts), the ONE place this predicate lives, so
              COM-944's truer signal is a one-line change here, not a hunt
              across every render site. */}
          {isInvalidSource(row) && (
            <Tooltip title="Invalid source">
              {/* COM-647 Task 5: e2e-only hook (`invalid-source-badge`) - the
                  badge has no accessible name of its own, and the icon
                  element is a double-nested <svg> (uikit/icons/Warning.tsx
                  wraps a raw <svg> inside MUI's own SvgIcon <svg>), so a
                  plain `data-testid` is more robust for the e2e suite than
                  guessing at DOM/svg ordering. */}
              <Box data-testid="invalid-source-badge" sx={{ display: 'flex', flexShrink: 0 }}>
                <Warning sx={{ width: 16, height: 16, color: '#FC4D2B' }} />
              </Box>
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Toggle
            checked={row.isMonitored}
            disabled={togglingIds.has(row.id) || !canManage}
            onChange={(e) => handleToggleMonitored(row, e.target.checked)}
            inputProps={{ 'aria-label': `toggle monitoring for ${sourceDisplayName(row)}` }}
          />
          <Typography fontSize={14} color="text.secondary">
            {row.isMonitored ? 'Active' : 'Inactive'}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'createdAt',
      title: 'Creation Date',
      sortable: true,
      render: (row) => (
        <Typography fontSize={14} color="text.secondary">
          {formatSourceCreatedAt(row.createdAt)}
        </Typography>
      ),
    },
    // A VIEWER gets a read-only table (see `canManage`'s own comment above) -
    // the actions column has nothing but mutating entries (Edit/Delete), so
    // it's omitted entirely rather than rendered disabled, same as
    // `UserSettingsDialog.tsx`'s own `{canManageProject() && <TableCell
    // align="center">Actions</TableCell>}`.
    ...(canManage
      ? [
          {
            key: 'actions',
            title: '',
            width: 56,
            render: (row: ManagedSource) => (
              <ActionsMenu
                ariaLabel={`actions for ${sourceDisplayName(row)}`}
                items={[
                  { label: 'Edit', onClick: () => openEditDialog(row) },
                  { label: 'Delete', danger: true, onClick: () => requestDelete([row.id]) },
                ]}
              />
            ),
          },
        ]
      : []),
  ];

  if (initialLoading) {
    return <PageLoader />;
  }

  return (
    <Box
      data-testid="sources-settings-page"
      sx={{ display: 'flex', flexDirection: 'column', flex: 1, p: 4, gap: 3, overflow: 'auto' }}
    >
      {/* COM-647 (Artur, 2026-08-22): this slot held a "Settings / Sources"
        * breadcrumb, which was dead weight - Figma draws that breadcrumb in
        * ComplyControl's own chrome OUTSIDE the iframe, so ours only ever
        * duplicated it. It is replaced by the way back, which the page had
        * none of: the app header is deliberately not rendered in an embedded
        * session (it would duplicate CC's chrome - see the embedded-mode
        * e2e), so a header-only affordance leaves the iframe with no exit at
        * all. In-page, it works in both modes. */}
      <Box>
        <Button variant="text" onClick={() => navigate('/')} startIcon={<ChevronLeft />}>
          All articles
        </Button>
      </Box>

      <Typography variant="h3">Sources</Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <SearchInput value={searchInput} onChange={setSearchInput} placeholder="Search sources" />
          <StatusFilterMenu
            value={filters.statusFilter}
            onChange={(statusFilter) => setFilters((current) => ({ ...current, statusFilter }))}
          />
          <DateRangePicker
            value={{ preset: datePreset, range: filters.dateRange }}
            onChange={(value) => {
              setDatePreset(value.preset);
              setFilters((current) => ({ ...current, dateRange: value.range }));
            }}
          />
        </Box>

        {/* A VIEWER gets neither button: no selection is ever possible for
            them (`selectable={canManage}` below), so "Delete Sources" never
            applies, and "Add source" is a mutation the backend would refuse
            anyway (same reasoning as `AllArticlesPage.tsx`'s own
            `canAddSource` hiding its "Add new source" button). */}
        {canManage &&
          (toolbarAction === 'delete' ? (
            <Button
              variant="primary"
              onClick={() => requestDelete(Array.from(selected))}
              sx={{ backgroundColor: '#FC4D2B', '&:hover': { backgroundColor: '#d63c1e' } }}
            >
              Delete Sources
            </Button>
          ) : (
            <Button variant="primary" startIcon={<Plus sx={{ width: 14, height: 14 }} />} onClick={openAddDialog}>
              Add source
            </Button>
          ))}
      </Box>

      <DataTable
        columns={columns}
        rows={rows}
        getRowId={(row) => row.id}
        selectable={canManage}
        selected={selected}
        onSelectedChange={setSelected}
        sortBy={filters.sortBy}
        sortDir={filters.sortDir}
        onSortChange={(key, dir) =>
          setFilters((current) => ({ ...current, sortBy: key as SourcesTableFilters['sortBy'], sortDir: dir }))
        }
        onEndReached={canFetchMore ? onEndReached : undefined}
        emptyState={
          <EmptyState
            title={noFiltersApplied ? 'No sources yet' : 'No sources match your filters'}
            description={
              noFiltersApplied
                ? 'Add a source to start monitoring it for new regulatory articles.'
                : 'Try adjusting your search, status or date filters.'
            }
            action={
              noFiltersApplied && canManage ? (
                <Button variant="primary" startIcon={<Plus sx={{ width: 14, height: 14 }} />} onClick={openAddDialog}>
                  Add source
                </Button>
              ) : undefined
            }
          />
        }
      />

      <SourceFormDialog
        open={formDialog !== null}
        mode={formDialog?.mode ?? 'add'}
        source={formDialog?.source ?? null}
        submitting={formSubmitting}
        refusalError={formRefusalError}
        onClose={closeFormDialog}
        onSubmitAdd={handleSubmitAdd}
        onSubmitEdit={handleSubmitEdit}
      />

      <ConfirmDialog
        open={deleteTargetIds !== null}
        title={deleteTargetIds && deleteTargetIds.length > 1 ? 'Delete sources' : 'Delete source'}
        message={
          deleteTargetIds && deleteTargetIds.length > 1
            ? `Are you sure you want to delete these ${deleteTargetIds.length} sources? This cannot be undone.`
            : 'Are you sure you want to delete this source? This cannot be undone.'
        }
        confirmLabel="Delete"
        danger
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </Box>
  );
};

export default SourcesSettingsPage;
